'use client';

import { useEffect, useMemo, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { PATHS } from 'routes/routes.constants';

import useQueryParams from '@polyai/common/hooks/useQueryParams';

import OTError from 'components/organisms/OTError/OTError';
import OTStatusScreen from 'components/organisms/OTStatusScreen/OTStatusScreen';
import { usePendo } from 'hooks/usePendo';
import {
  buildAppState,
  getCallbackUrl,
  getOtEnv,
  persistOtParamsToLocalStorage,
  readAndValidateOtParams,
  startAuth0Redirect,
} from 'lib/opentable';

export default function OtLoginPage() {
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const { logout } = useAuth0();

  const queryParams = useQueryParams();
  const region = queryParams.get('region') ?? undefined;
  const pendoAdditionalInfo = useMemo(
    () => (region ? { country: region } : undefined),
    [region],
  );
  // Initialize Pendo for anonymous tracking
  usePendo({ additionalInfo: pendoAdditionalInfo });

  useEffect(() => {
    let cancelled = false;

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    const run = async () => {
      try {
        const { domain, audience, scope } = getOtEnv();
        if (!domain) {
          if (!cancelled) {
            setErrMsg('Auth0 credentials misconfigured.');
          }
          return;
        }

        const href = window.location.href;
        const callbackUrl = getCallbackUrl(PATHS.OPENTABLE_CALLBACK);

        const params = readAndValidateOtParams(href);
        if (!params.ok) {
          if (!cancelled) {
            setErrMsg(params.error);
          }
          return;
        }

        persistOtParamsToLocalStorage(params.data.incomingEntries);

        const appState = buildAppState(href, params.data.incomingEntries);

        await logout();

        await startAuth0Redirect({
          domain,
          audience,
          scope,
          callbackUrl,
          clientId: process.env.NEXT_PUBLIC_AUTH0_CLIENT_ID!,
          appState,
        });
      } catch {
        if (!cancelled) {
          setErrMsg('Something went wrong starting login.');
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [logout]);

  if (errMsg) {
    return (
      <OTError description={errMsg} retryLabel="Retry" title="Login Failed" />
    );
  }

  return <OTStatusScreen status="Redirecting..." />;
}
